export const MAX_TABLE_FIELD_COLUMNS = 5;

export const pickFirst = (obj, ...keys) => {
  if (!obj || typeof obj !== 'object') {
    return undefined;
  }

  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== '') {
      return obj[key];
    }
  }

  return undefined;
};

export const getFormIdentity = (form) => ({
  id: String(pickFirst(form, 'id', 'Id') || ''),
  slug: String(pickFirst(form, 'slug', 'Slug', 'name', 'Name') || '')
});

export const getEntityFields = (entity) => {
  const fields = entity?.fields ?? entity?.Fields;
  return Array.isArray(fields) ? fields : [];
};

const parseJsonValue = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const getSubmissionJsonData = (submission) => {
  const raw = submission?.jsonData ?? submission?.JsonData;
  return parseJsonValue(raw);
};

export const submissionFieldsToObject = (jsonData) => {
  const data = parseJsonValue(jsonData);

  if (!data) {
    return {};
  }

  if (Array.isArray(data)) {
    return data.reduce((accumulator, field) => {
      const slug = field?.slug || field?.Slug;
      if (slug) {
        accumulator[slug] = field.value ?? field.Value ?? '';
      }

      return accumulator;
    }, {});
  }

  if (typeof data === 'object') {
    return { ...data };
  }

  return {};
};

export const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

export const getJsonDataFields = (jsonData) => {
  const data = parseJsonValue(jsonData);

  if (!data) {
    return [];
  }

  if (Array.isArray(data)) {
    return data
      .filter((field) => field?.slug || field?.Slug)
      .map((field) => {
        const slug = field.slug || field.Slug;
        return {
          slug,
          name: field.name || field.Name || slug,
          type: field.type || field.Type,
          value: field.value ?? field.Value
        };
      });
  }

  if (typeof data === 'object') {
    return Object.entries(data).map(([slug, value]) => ({
      slug,
      name: slug,
      type: typeof value,
      value
    }));
  }

  return [];
};

export const extractFieldColumns = (submissions, limit = MAX_TABLE_FIELD_COLUMNS) => {
  const columns = [];
  const seen = new Set();

  submissions.forEach((submission) => {
    const fields = getJsonDataFields(getSubmissionJsonData(submission));

    fields.forEach((field) => {
      if (field?.slug && !seen.has(field.slug)) {
        seen.add(field.slug);
        columns.push({
          slug: field.slug,
          name: field.name || field.slug,
          type: field.type
        });
      }
    });
  });

  return columns.slice(0, limit);
};

export const getSubmissionFieldValue = (submission, slug) => {
  const fields = getJsonDataFields(getSubmissionJsonData(submission));
  const field = fields.find((item) => item.slug === slug);

  return formatFieldValue(field?.value);
};

export const buildFieldLabels = (jsonData) => {
  const fields = getJsonDataFields(jsonData);

  return fields.reduce((accumulator, field) => {
    if (field?.slug) {
      accumulator[field.slug] = field.name || field.slug;
    }

    return accumulator;
  }, {});
};
